import { Show, createEffect, createSignal, onCleanup } from "solid-js";
import type { ClockParts } from "../game/clock";
import { t } from "../strings";

// A countdown that ticks by itself: the text is read again four times a second and the span's text changes
// only when it does, once a second, so nothing around it is touched for a clock (a phone playing music heard
// the table's ticks). The parent makes a new one for a new clock.
export function Countdown(props: { class: string; text: (now: number) => string }) {
  const [shown, setShown] = createSignal(props.text(performance.now()));
  createEffect(() => setShown(props.text(performance.now()))); // a new clock: at once
  const id = setInterval(() => setShown(props.text(performance.now())), 250);
  onCleanup(() => clearInterval(id));
  return <span class={props.class}>{shown()}</span>;
}

// The seat's own clock (audit 2026-10-14: "10+20" floated unlabelled and was never explained): a clock's mark,
// the move's seconds and, smaller, the bank's, said in words to a screen reader and on hover; once the move's
// time is spent, the bank alone in the warning colour. It ticks as the countdown above, each number changing
// only when it does.
export function OwnClock(props: { parts: (now: number) => ClockParts }) {
  const first = props.parts(performance.now());
  const [base, setBase] = createSignal(first.base);
  const [bank, setBank] = createSignal(first.bank);
  const tick = () => {
    const p = props.parts(performance.now());
    setBase(p.base);
    setBank(p.bank);
  };
  createEffect(tick); // a new clock: at once
  const id = setInterval(tick, 250);
  onCleanup(() => clearInterval(id));
  const words = () => t.ownClock(base(), bank());
  return (
    <span class={`own-clock${base() === null ? " on-bank" : ""}`} role="timer" aria-label={words()} title={words()}>
      <svg class="clock-mark" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="9" r="6" fill="none" stroke="currentColor" stroke-width="1.6" />
        <path d="M8 5.5V9l2.2 1.6M6.5 1.5h3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
      </svg>
      <Show when={base() !== null} fallback={<span>{bank()}</span>}>
        <span>{base()}</span>
        <Show when={bank() !== null}>
          <small class="bank">+ {bank()}</small>
        </Show>
      </Show>
    </span>
  );
}
